// ブランド（サービス名・運営会社名など）の正本。
//
// ■ なぜ設定にするか
// 同じ機能を別コミュニティ向けに立ち上げるとき、コードを分岐させると
// 修正が二重管理になり、必ず片方が古くなる。1 つのコードベースのまま
// **環境ごとに値だけ差し替える**ためにここへ集める。
//
// ■ 既定値は「現在の CFL の表示そのもの」
// 設定を何も入れなければ出力は 1 文字も変わらない、という状態を保つこと。
// これが本番へのデグレを防ぐ唯一の担保なので、既定値を「それらしい汎用名」に
// 変えてはいけない（`brand.test.ts` が現行の文字列を直接持っていて検出する）。
//
// ■ 参照の仕方
//   client: `src/client/lib/brand.ts`  … ビルド時の VITE_BRAND_ID を見る
//   server: `src/server/lib/brand.ts`  … Worker の env.BRAND_ID を見る
// 直接この既定値を画面やメールで使わないこと（差し替えが効かなくなる）。
//
// ■ なぜ「プリセット + ID」なのか
// ブランドは 7 項目ある。これを wrangler.toml（サーバ用）と deploy workflow
// （クライアントのビルド用）の両方に並べると、**同じ値を 2 箇所で管理する**
// ことになり、片方だけ直したときに「画面はオフレコ、メールは CFL」という
// 食い違いが起きる。値そのものはこのファイルに 1 つだけ置き、
// 設定側は `BRAND_ID` を 1 つ渡すだけにする。

export interface Brand {
  /** 略称。ロゴの大文字部分（例: CFL） */
  shortName: string;
  /** 正式名称（例: CloseFamilyLounge） */
  name: string;
  /** 運営会社名（利用規約・表記） */
  companyName: string;
  /** サイト名。ブラウザタブ・PWA 名（例: CloseFamilyLounge 会員サイト） */
  siteName: string;
  /** meta description。検索結果・共有時に出る 1 文 */
  description: string;
  /** LINE 公式アカウントの表示名（友だち追加の案内文で使う） */
  lineAccountName: string;
  /** 問い合わせ先メールアドレス。別ブランドで元の窓口に飛ばさないため必須 */
  supportEmail: string;
  /** 一言でどんな集まりかを表す文。ヒーロー画像の代替テキストに使う */
  tagline: string;
  /** ブラウザ UI / PWA のテーマ色 (#RRGGBB) */
  themeColor: string;
  /** PWA の起動時背景色 (#RRGGBB) */
  backgroundColor: string;
}

export const DEFAULT_BRAND: Brand = {
  shortName: "CFL",
  name: "CloseFamilyLounge",
  companyName: "株式会社CloseFamily",
  siteName: "CloseFamilyLounge 会員サイト",
  description:
    "CloseFamilyLounge (CFL) 会員専用サイト。経営者コミュニティのイベント申込・受付QR・コンテンツ視聴・お知らせ閲覧。",
  lineAccountName: "CloseFamilyLounge",
  supportEmail: "info@teamcfl.com",
  tagline: "経営者のみ在籍するコミュニティ / 課題解決と意思決定の質とスピード。",
  themeColor: "#0B1F44",
  backgroundColor: "#FFFFFF",
};

/**
 * コミュニティごとのブランド値。
 *
 * 新しいコミュニティを足すときはここに 1 ブロック足し、
 * wrangler.toml の `BRAND_ID` と deploy workflow の `VITE_BRAND_ID` に
 * そのキーを入れる。**値を設定ファイル側へ書かないこと。**
 */
export const BRAND_PRESETS: Record<string, Brand> = {
  cfl: DEFAULT_BRAND,
  ofreco: {
    shortName: "オフレコ",
    name: "オフレコ",
    companyName: "オフレコ運営事務局",
    siteName: "オフレコ 会員サイト",
    description:
      "オフレコ（経営者美食倶楽部）会員専用サイト。イベント申込・受付QR・コンテンツ視聴・お知らせ閲覧。",
    lineAccountName: "オフレコ",
    supportEmail: "info@offrecoo.com",
    tagline: "経営者美食倶楽部",
    // ロゴのゴールドと生成りに合わせる（CFL はネイビー基調なので別値）
    themeColor: "#B8863F",
    backgroundColor: "#FFFDF7",
  },
};

/**
 * ブランドを決める。
 *
 * 1. `id` がプリセットにあればそれを土台にする（無い ID は既定値に落とす。
 *    typo で画面からブランド名が消えるより、CFL 表示のまま出る方が安全）
 * 2. 個別の上書きがあれば重ねる（緊急の差し替え用。通常は使わない）
 *
 * 未設定・空文字はどちらも既定値に落とす。設定を「空で置いた」場合に
 * ブランド名が消えた画面を出さないため（var は空文字になりやすい）。
 */
export function resolveBrand(
  over: Partial<Record<keyof Brand, string | undefined>> = {},
  id?: string,
): Brand {
  const base =
    (typeof id === "string" && BRAND_PRESETS[id.trim()]) || DEFAULT_BRAND;
  const pick = (k: keyof Brand) => {
    const v = over[k];
    return typeof v === "string" && v.trim() !== "" ? v : base[k];
  };
  return {
    shortName: pick("shortName"),
    name: pick("name"),
    companyName: pick("companyName"),
    siteName: pick("siteName"),
    description: pick("description"),
    lineAccountName: pick("lineAccountName"),
    supportEmail: pick("supportEmail"),
    tagline: pick("tagline"),
    themeColor: pick("themeColor"),
    backgroundColor: pick("backgroundColor"),
  };
}
